import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SwipaWordmark } from '@/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authApi } from '@/api/auth';
import { extractError } from '@/api/client';

const schema = z.object({
  // Email и username нормализуем в lower-case ДО валидации — так юзер,
  // случайно нажавший Caps Lock, не получит CONFLICT при попытке зарегистрировать
  // «уже занятый» (а на деле — просто другой регистр) email/логин.
  email: z.string().trim().toLowerCase().email('Некорректный email'),
  username: z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Минимум 3 символа')
    .max(32)
    .regex(/^[a-z0-9_-]+$/, 'Только строчные латинские буквы, цифры, _ и -'),
  displayName: z.string().min(1, 'Обязательное поле').max(64),
  password: z
    .string()
    .min(8, 'Минимум 8 символов')
    .regex(/[A-Z]/, 'Должна быть заглавная буква')
    .regex(/[0-9]/, 'Должна быть цифра'),
  confirmPassword: z.string(),
  tosAccepted: z.literal(true, { errorMap: () => ({ message: 'Необходимо принять Соглашение' }) }),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Пароли не совпадают',
  path: ['confirmPassword'],
});

type FormData = z.infer<typeof schema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await authApi.register(data);
      setSuccess(true);
    } catch (err) {
      setServerError(extractError(err));
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="text-5xl mb-4">📬</div>
          <h2 className="text-xl font-bold text-surface-50 mb-2">Проверьте почту</h2>
          <p className="text-surface-400 text-sm mb-6">
            Мы отправили ссылку для подтверждения. Нажмите на неё чтобы активировать аккаунт.
          </p>
          <Link to="/auth/login" className="btn-primary">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <SwipaWordmark size={44} base="#ffffff" accent="#C5243A" />
          <p className="text-surface-400 mt-3 text-sm">Создать аккаунт</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-surface-900 border border-surface-800 rounded-xl p-6 space-y-4"
        >
          {serverError && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {serverError}
            </div>
          )}

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Email</label>
            <input
              {...register('email')}
              className="irc-input lowercase"
              placeholder="ivan@example.com"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Имя пользователя</label>
            <input
              {...register('username')}
              className="irc-input lowercase"
              placeholder="ivan_ivanov"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {errors.username && (
              <p className="text-red-400 text-xs mt-1">{errors.username.message}</p>
            )}
          </div>

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Отображаемое имя
            </label>
            <input {...register('displayName')} className="irc-input" placeholder="Иван Иванов" />
            {errors.displayName && (
              <p className="text-red-400 text-xs mt-1">{errors.displayName.message}</p>
            )}
          </div>

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Пароль</label>
            <input
              {...register('password')}
              type="password"
              className="irc-input"
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Подтвердите пароль
            </label>
            <input
              {...register('confirmPassword')}
              type="password"
              className="irc-input"
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="text-red-400 text-xs mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('tosAccepted')}
                className="mt-0.5 accent-brand-400"
              />
              <span className="text-surface-400 text-xs leading-relaxed">
                Я ознакомился и принимаю{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-brand-400 hover:text-brand-300 underline"
                >
                  Пользовательское соглашение
                </Link>
                {' '}и{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-brand-400 hover:text-brand-300 underline"
                >
                  Политику конфиденциальности
                </Link>
              </span>
            </label>
            {errors.tosAccepted && (
              <p className="text-red-400 text-xs mt-1">{errors.tosAccepted.message}</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Создать аккаунт'
            )}
          </button>

          <p className="text-center text-surface-400 text-sm">
            Уже есть аккаунт?{' '}
            <Link to="/auth/login" className="text-brand-400 hover:text-brand-300">
              Войти
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
